import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '@ups/xplat/core';
import { UpsAuthService } from '@ups/xplat/base/auth';
import {
  BridgeRegisterRequest,
  BridgeRegisterResponse,
  BRIDGE_REGISTER_PATH,
} from '../models/bridge.models';
import { BridgeJwtProvider } from './bridge-auth.interceptor';

/**
 * Platform-neutral {@link BridgeJwtProvider} for the self-hosted-Entra apps
 * (Ryn's Roost Mobile is the pilot; any lean Entra app that boots through
 * {@link UpsAuthService} instead of MSAL can reuse this without change).
 *
 * The web Roost provider ({@link RoostBridgeAuthService}) mints its Entra token
 * through MsalService; this one takes the token {@link UpsAuthService} already
 * holds from the interactive AD sign-in (aud = the app's own Entra
 * registration, scope Access.All) and exchanges it at the bridge for an agent
 * JWT. The Entra token must be minted for the app's own API resource
 * (aud api://<clientId>), never Microsoft Graph: a Graph-audience token is
 * rejected at the register EntraID auth check. No Adaptive Card and no PSK
 * are involved on this path.
 *
 * Registered under {@link BRIDGE_JWT_PROVIDER}; the {@link BridgeAuthInterceptor}
 * calls {@link getJwt} to attach `Authorization: Bearer <jwt>` to every gated
 * bridge request. Without a provider the interceptor forwards gated calls with
 * no Authorization header, which is why the mobile bridge tabs 401'd before
 * this existed (WI #31630).
 *
 * The bridge JWT is cached in memory; a single in-flight exchange is shared so
 * the parallel health/instances/messages calls a tab fires on load do not each
 * POST /register.
 *
 * A 401/403 from /register means the bridge refused this user, and retrying on
 * the next poll will not change that. The rejection is remembered per instance
 * id and the next exchange waits out an exponential backoff (30s doubling, capped
 * at 15 min), so a rejected phone does not POST /register on every poll
 * (Bug #34558: one rejected seat sent 2,867 in 2.6h). Network and 5xx failures
 * still retry on the next call.
 */
const REJECTION_BACKOFF_BASE_MS = 30_000;
const REJECTION_BACKOFF_MAX_MS = 15 * 60_000;

@Injectable({ providedIn: 'root' })
export class UpsAuthBridgeJwtProvider implements BridgeJwtProvider {
  private readonly upsAuth = inject(UpsAuthService);
  private readonly http = inject(HttpClient);

  private cachedJwt: string | null = null;
  private inflight: Promise<string | null> | null = null;
  private rejection: {
    instanceId: string;
    count: number;
    retryAt: number;
  } | null = null;

  async getJwt(): Promise<string | null> {
    if (this.cachedJwt) return this.cachedJwt;
    if (this.inflight) return this.inflight;

    this.inflight = this.exchange().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  async refreshJwt(): Promise<string | null> {
    // Drop the cached bridge JWT so the next exchange re-registers. The Entra
    // token itself auto-refreshes inside getAccessToken() when it has expired,
    // so a 401 caused by an expired bridge JWT recovers without a re-login.
    this.cachedJwt = null;
    return this.getJwt();
  }

  private async exchange(): Promise<string | null> {
    const bridgeBase = environment.urls?.bridgeAPI;
    if (!bridgeBase) {
      // No configured bridge endpoint — nothing to exchange against. The
      // interceptor short-circuits gated calls when this resolves to null.
      return null;
    }

    // The Entra access token from the interactive AD sign-in (auto-refreshed
    // when expired). firstValueFrom rejects when there is no valid session and
    // no refresh token, so a signed-out state resolves to null rather than
    // firing a doomed unauthenticated /register.
    let entraToken: string;
    try {
      entraToken = await firstValueFrom(this.upsAuth.getAccessToken());
    } catch {
      return null;
    }
    if (!entraToken) return null;

    const user = this.upsAuth.currentUser();
    const email = user?.email ?? user?.adUserName ?? undefined;
    const displayName = user?.displayName || email || 'user';
    const instanceId = mobileRoostInstanceId(email);
    if (
      this.rejection?.instanceId === instanceId &&
      Date.now() < this.rejection.retryAt
    ) {
      return null;
    }

    const body: BridgeRegisterRequest = {
      instanceId,
      role: 'mobile-ui',
      layer: 'channel',
      machineName: instanceId,
      hostname: `Ryn's Roost Mobile (${displayName})`,
    };

    try {
      const res = await firstValueFrom(
        this.http.post<BridgeRegisterResponse>(
          `${bridgeBase}${BRIDGE_REGISTER_PATH}`,
          body,
          { headers: { Authorization: `Bearer ${entraToken}` } }
        )
      );
      // A token-less 200 is an anonymous accept, not an authenticated one, so
      // it must be treated as a failure rather than cached as a JWT.
      this.cachedJwt = res?.token ?? null;
      if (this.cachedJwt) this.rejection = null;
      return this.cachedJwt;
    } catch (err) {
      if (
        err instanceof HttpErrorResponse &&
        (err.status === 401 || err.status === 403)
      ) {
        this.recordRejection(instanceId);
      }
      return null;
    }
  }

  private recordRejection(instanceId: string): void {
    const count =
      this.rejection?.instanceId === instanceId ? this.rejection.count + 1 : 1;
    const delayMs = Math.min(
      REJECTION_BACKOFF_BASE_MS * 2 ** (count - 1),
      REJECTION_BACKOFF_MAX_MS
    );
    this.rejection = { instanceId, count, retryAt: Date.now() + delayMs };
  }
}

/**
 * Bridge instance id for a mobile Roost seat: `roost-mobile-{slug}` derived from
 * the signed-in user's email local-part. Namespaced with `mobile` so a user's
 * phone does not overwrite their web `roost-{slug}` registry row, and derived
 * from identity rather than a device id so the provider stays platform-neutral
 * (no @nativescript/core dependency in this base lib). The bridge enforces the
 * role-personality dash, which the two literal segments satisfy.
 */
export function mobileRoostInstanceId(email: string | undefined): string {
  const local = (email ?? 'user').split('@')[0];
  const slug = local
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `roost-mobile-${slug || 'user'}`;
}
